import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
    ShieldCheck, Sparkles, Eye, Clock, CheckCircle2, EyeOff,
    AlertCircle, Loader2, Info, X, RefreshCcw, Ban
} from 'lucide-react';
import { toast } from 'sonner';
import ReseauLayout from '../../components/reseau/ReseauLayout';
import useCockpitAction from '../../lib/useCockpitAction';

// ============================================================
// Constantes UI
// ============================================================
const STATUS_BADGE = {
    proposed: { label: 'À examiner', color: 'bg-amber-100 text-amber-800 border-amber-200' },
    consented: { label: 'Consentie', color: 'bg-sky-100 text-sky-800 border-sky-200' },
    published: { label: 'Publiée', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    hidden: { label: 'Masquée', color: 'bg-slate-200 text-slate-700 border-slate-300' },
    expired: { label: 'Expirée', color: 'bg-orange-100 text-orange-800 border-orange-200' },
    rejected_by_professional: { label: 'Refusée', color: 'bg-rose-100 text-rose-800 border-rose-200' },
    revoked: { label: 'Révoquée (admin)', color: 'bg-rose-200 text-rose-900 border-rose-300' },
    superseded: { label: 'Remplacée', color: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const CATEGORY_LABEL = {
    identity: 'Identité',
    experience: 'Expérience',
    local: 'Implantation locale',
    activity: 'Activité',
    quality: 'Qualité',
};

const SOURCE_LABEL = {
    declared: 'Déclarée par le pro',
    agency_confirmed: 'Confirmée par l\'agence',
    homly_verified: 'Vérifiée par Homly',
    cockpit_calculated: 'Calculée depuis l\'activité Homly',
    review_aggregated: 'Issue d\'avis vérifiés',
};

const TABS = [
    { key: 'to_review', label: 'À examiner', statuses: ['proposed'] },
    { key: 'published', label: 'Publiées', statuses: ['consented', 'published'] },
    { key: 'hidden', label: 'Masquées', statuses: ['hidden'] },
    { key: 'expired', label: 'Expirées', statuses: ['expired', 'superseded'] },
    { key: 'rejected', label: 'Refusées / Révoquées', statuses: ['rejected_by_professional', 'revoked'] },
];

// ============================================================
// Composant fiche
// ============================================================
function ProofCard({ proof, onConsent, onPublish, onReject, onHide, onRepublish, onPreview, onOpenDetail }) {
    const st = STATUS_BADGE[proof.publication_status] || STATUS_BADGE.proposed;
    const expired = proof.expires_at && proof.expires_at < new Date().toISOString();
    const expiresSoon = !expired && proof.expires_at &&
        new Date(proof.expires_at).getTime() - Date.now() < 14 * 24 * 3600 * 1000;

    return (
        <div data-testid={`proof-card-${proof.proof_type}`}
             className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-[#681793]/30 transition-colors">
            <div className="flex items-start justify-between gap-4 mb-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                        <Badge className={`${st.color} border text-xs`} data-testid={`proof-status-${proof.id}`}>
                            {st.label}
                        </Badge>
                        <Badge className="bg-slate-100 text-slate-700 border-slate-200 text-xs">
                            {CATEGORY_LABEL[proof.category] || proof.category}
                        </Badge>
                        {expiresSoon && (
                            <Badge className="bg-orange-50 text-orange-700 border-orange-200 text-xs">
                                <Clock className="w-3 h-3 mr-0.5 inline" /> Expire bientôt
                            </Badge>
                        )}
                        {proof.supersedes_id && (
                            <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs">
                                Nouvelle version
                            </Badge>
                        )}
                    </div>
                    <h3 className="text-base font-semibold text-slate-900">{proof.title}</h3>
                    <p className="text-sm text-slate-600 mt-1">{proof.public_description}</p>
                </div>
            </div>

            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-500 mb-4">
                <div><dt className="inline text-slate-400">Source : </dt><dd className="inline">{SOURCE_LABEL[proof.source_type] || proof.source_type}</dd></div>
                <div><dt className="inline text-slate-400">Vérification : </dt><dd className="inline">{proof.verification_status}</dd></div>
                {proof.period_start && (
                    <div><dt className="inline text-slate-400">Période : </dt><dd className="inline">depuis {new Date(proof.period_start).toLocaleDateString('fr-FR')}</dd></div>
                )}
                {proof.expires_at && (
                    <div><dt className="inline text-slate-400">Expire le : </dt><dd className="inline">{new Date(proof.expires_at).toLocaleDateString('fr-FR')}</dd></div>
                )}
                {proof.generated_at && (
                    <div><dt className="inline text-slate-400">Générée le : </dt><dd className="inline">{new Date(proof.generated_at).toLocaleDateString('fr-FR')}</dd></div>
                )}
            </dl>

            <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => onPreview(proof)}
                        data-testid={`btn-preview-${proof.id}`}>
                    <Eye className="w-4 h-4 mr-1.5" /> Aperçu public
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onOpenDetail(proof)}
                        data-testid={`btn-detail-${proof.id}`}>
                    <Info className="w-4 h-4 mr-1.5" /> Comprendre
                </Button>

                {proof.publication_status === 'proposed' && (
                    <>
                        <Button size="sm" onClick={() => onConsent(proof)}
                                className="bg-[#681793] hover:bg-[#7d2aac]"
                                data-testid={`btn-consent-${proof.id}`}>
                            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Consentir
                        </Button>
                        <Button size="sm" variant="outline"
                                onClick={() => onReject(proof)}
                                data-testid={`btn-reject-${proof.id}`}>
                            <Ban className="w-4 h-4 mr-1.5" /> Refuser
                        </Button>
                    </>
                )}
                {proof.publication_status === 'consented' && (
                    <Button size="sm" onClick={() => onPublish(proof)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid={`btn-publish-${proof.id}`}>
                        <Sparkles className="w-4 h-4 mr-1.5" /> Publier
                    </Button>
                )}
                {proof.publication_status === 'published' && (
                    <Button size="sm" variant="outline" onClick={() => onHide(proof)}
                            data-testid={`btn-hide-${proof.id}`}>
                        <EyeOff className="w-4 h-4 mr-1.5" /> Masquer
                    </Button>
                )}
                {proof.publication_status === 'hidden' && proof.consent_status === 'granted' && !expired && (
                    <Button size="sm" onClick={() => onRepublish(proof)}
                            className="bg-[#681793] hover:bg-[#7d2aac]"
                            data-testid={`btn-republish-${proof.id}`}>
                        <RefreshCcw className="w-4 h-4 mr-1.5" /> Republier
                    </Button>
                )}
            </div>
        </div>
    );
}

// ============================================================
// Modale d'aperçu public
// ============================================================
function PreviewModal({ proof, onClose, onConfirmConsent }) {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [confirmChecked, setConfirmChecked] = useState(false);

    useEffect(() => {
        if (!proof) return;
        setLoading(true);
        api.get(`/proofs/me/preview/${proof.id}`)
            .then(r => setPreview(r.data))
            .catch(() => toast.error('Impossible de charger l\'aperçu'))
            .finally(() => setLoading(false));
    }, [proof]);

    if (!proof) return null;
    const canConsent = proof.publication_status === 'proposed';

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
             data-testid="preview-modal" onClick={onClose}>
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                 onClick={e => e.stopPropagation()}>
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                    <h2 className="text-lg font-bold text-slate-900">Aperçu public</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700"
                            data-testid="preview-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-6 space-y-5">
                    {loading ? (
                        <Loader2 className="w-6 h-6 animate-spin text-[#681793] mx-auto" />
                    ) : preview ? (
                        <>
                            <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                                <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Ce que verra le vendeur</p>
                                <h3 className="text-base font-semibold text-slate-900 mb-1">{preview.preview.title}</h3>
                                <p className="text-sm text-slate-600">{preview.preview.public_description}</p>
                                <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
                                    <span>Source : {SOURCE_LABEL[preview.preview.source_type]}</span>
                                    {preview.preview.period_start && (
                                        <span>· Période : depuis {new Date(preview.preview.period_start).toLocaleDateString('fr-FR')}</span>
                                    )}
                                </div>
                            </div>
                            <div className="text-sm text-slate-700 space-y-2">
                                <p><strong>Pourquoi cette preuve :</strong> {preview.preview.explainer}</p>
                                <p className="text-slate-500"><strong>Limites :</strong> {preview.preview.limits}</p>
                                <p className="text-xs text-slate-400">
                                    Aucune donnée client (nom, adresse, prix, mandat, contrat…) n&apos;est exposée.
                                    Vous pouvez masquer cette preuve à tout moment.
                                </p>
                            </div>
                            {canConsent && (
                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                                    <label className="flex items-start gap-3 cursor-pointer">
                                        <input type="checkbox" className="mt-1"
                                               checked={confirmChecked}
                                               onChange={e => setConfirmChecked(e.target.checked)}
                                               data-testid="consent-checkbox" />
                                        <span className="text-sm text-slate-800">
                                            Je confirme vouloir rendre cette preuve visible sur mon profil public.
                                        </span>
                                    </label>
                                    <Button disabled={!confirmChecked}
                                            onClick={() => onConfirmConsent(proof)}
                                            className="bg-[#681793] hover:bg-[#7d2aac] w-full"
                                            data-testid="confirm-consent-btn">
                                        <CheckCircle2 className="w-4 h-4 mr-1.5" /> Donner mon consentement
                                    </Button>
                                </div>
                            )}
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

// ============================================================
// Page principale
// ============================================================
export default function ReseauMyProofsPage() {
    const [me, setMe] = useState(null);
    const [proofs, setProofs] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('to_review');
    const [previewing, setPreviewing] = useState(null);
    const runAction = useCockpitAction();

    // Helper: optimistic status patch on a proof in local list + rollback function.
    const patchStatusOptimistic = (proofId, nextStatus) => {
        let snapshot = null;
        setProofs(prev => {
            snapshot = prev;
            return prev.map(p => p.id === proofId ? { ...p, publication_status: nextStatus } : p);
        });
        return () => { if (snapshot) setProofs(snapshot); };
    };

    // Lot 1D.1 — Focus depuis notification (?focus=<proof_id>)
    const focusedProofId = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('focus');
    }, []);

    useEffect(() => {
        api.get('/auth/me').then(r => setMe(r.data));
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/proofs/me');
            setProofs(r.data.items || []);
            setCounts(r.data.counts || {});
        } catch (e) {
            toast.error('Impossible de charger les preuves');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (me) load(); }, [me, load]);

    // Quand une preuve focus est passée par URL, ouvrir l'aperçu automatiquement
    useEffect(() => {
        if (!focusedProofId || proofs.length === 0) return;
        const found = proofs.find(p => p.id === focusedProofId);
        if (found) {
            // Bascule l'onglet pertinent
            const t = TABS.find(tt => tt.statuses.includes(found.publication_status));
            if (t) setTab(t.key);
            setPreviewing(found);
        }
    }, [focusedProofId, proofs]);

    const handleGenerate = async () => {
        try {
            const r = await api.post('/proofs/generate');
            toast.success(`${r.data.total} preuves analysées`);
            load();
        } catch {
            toast.error('Génération impossible');
        }
    };

    const handleConsent = async (proof) => {
        let revert = null;
        await runAction({
            key: `proof-consent-${proof.id}`,
            optimistic: () => { revert = patchStatusOptimistic(proof.id, 'consented'); },
            rollback:   () => { revert && revert(); },
            call:       () => api.post(`/proofs/${proof.id}/consent`),
            onSuccess:  () => { toast.success('Consentement enregistré. La preuve peut maintenant être publiée sur votre profil.'); setPreviewing(null); load(); },
            errorLabel: "Le consentement n'a pas été enregistré.",
        });
    };

    const handlePublish = async (proof) => {
        let revert = null;
        await runAction({
            key: `proof-publish-${proof.id}`,
            optimistic: () => { revert = patchStatusOptimistic(proof.id, 'published'); },
            rollback:   () => { revert && revert(); },
            call:       () => api.post(`/proofs/${proof.id}/publish`),
            onSuccess:  () => { toast.success('Cette preuve est désormais affichée sur votre profil public.'); load(); },
            errorLabel: "La publication n'a pas pu être enregistrée.",
        });
    };

    const handleReject = async (proof) => {
        if (!window.confirm('Refuser définitivement cette preuve ?')) return;
        let revert = null;
        await runAction({
            key: `proof-reject-${proof.id}`,
            optimistic: () => { revert = patchStatusOptimistic(proof.id, 'rejected_by_professional'); },
            rollback:   () => { revert && revert(); },
            call:       () => api.post(`/proofs/${proof.id}/reject`),
            onSuccess:  () => { toast.success('Preuve refusée. Elle reste privée et n\'apparaît pas sur votre profil.'); load(); },
            errorLabel: "Le refus n'a pas été enregistré.",
        });
    };

    const handleHide = async (proof) => {
        let revert = null;
        await runAction({
            key: `proof-hide-${proof.id}`,
            optimistic: () => { revert = patchStatusOptimistic(proof.id, 'hidden'); },
            rollback:   () => { revert && revert(); },
            call:       () => api.post(`/proofs/${proof.id}/hide`),
            onSuccess:  () => { toast.success('Preuve masquée de votre profil public. Vous pouvez la republier à tout moment.'); load(); },
            errorLabel: "Le masquage n'a pas été enregistré.",
        });
    };

    const handleRepublish = async (proof) => {
        let revert = null;
        await runAction({
            key: `proof-republish-${proof.id}`,
            optimistic: () => { revert = patchStatusOptimistic(proof.id, 'published'); },
            rollback:   () => { revert && revert(); },
            call:       () => api.post(`/proofs/${proof.id}/republish`),
            onSuccess:  () => { toast.success('Preuve à nouveau visible sur votre profil public.'); load(); },
            errorLabel: "La republication n'a pas été enregistrée.",
        });
    };

    const filtered = useMemo(() => {
        const t = TABS.find(t => t.key === tab);
        if (!t) return [];
        return proofs.filter(p => t.statuses.includes(p.publication_status));
    }, [proofs, tab]);

    return (
        <ReseauLayout
            headerTitle="Ma réputation Homly"
            headerSubtitle="Les preuves publiques de votre activité et de votre identité. Aucune donnée client n'est exposée. Chaque preuve doit être consentie avant d'apparaître sur votre profil public."
        >
            <main className="mx-auto px-4 sm:px-6 py-6 space-y-5" style={{ maxWidth: 'var(--reseau-maxw-page)' }}>
                <div className="flex items-center justify-end">
                    <Button onClick={handleGenerate} variant="outline" data-testid="btn-regenerate" className="reseau-btn reseau-btn-ghost">
                        <RefreshCcw className="w-4 h-4 mr-1.5" /> Actualiser mes preuves
                    </Button>
                </div>

                <div className="reseau-card p-2">
                    <div className="flex gap-1 overflow-x-auto" role="tablist">
                        {TABS.map(t => {
                            const n = proofs.filter(p => t.statuses.includes(p.publication_status)).length;
                            return (
                                <button
                                    key={t.key}
                                    onClick={() => setTab(t.key)}
                                    role="tab"
                                    aria-selected={tab === t.key}
                                    data-testid={`tab-${t.key}`}
                                    className="px-4 py-2 rounded-lg text-sm whitespace-nowrap font-medium transition-colors"
                                    style={tab === t.key ? {
                                        background: 'var(--reseau-violet)', color: '#fff'
                                    } : {
                                        color: 'var(--reseau-ink-soft)'
                                    }}>
                                    {t.label} {n > 0 && (
                                        <span className="ml-1 text-xs"
                                              style={tab === t.key ? { color: 'rgba(255,255,255,0.8)' } : { color: 'var(--reseau-ink-muted)' }}>
                                            ({n})
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-12">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--reseau-violet)' }} />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="reseau-empty" data-testid="empty-tab">
                        <AlertCircle className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--reseau-ink-faint)' }} />
                        <h3>Aucune preuve dans cette catégorie</h3>
                        <p>Cliquez sur « Actualiser mes preuves » pour rechercher les nouveaux signaux issus de votre cockpit.</p>
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="proofs-list">
                        {filtered.map(p => (
                            <ProofCard
                                key={p.id}
                                proof={p}
                                onConsent={handleConsent}
                                onPublish={handlePublish}
                                onReject={handleReject}
                                onHide={handleHide}
                                onRepublish={handleRepublish}
                                onPreview={(pp) => setPreviewing(pp)}
                                onOpenDetail={(pp) => setPreviewing(pp)}
                            />
                        ))}
                    </div>
                )}
            </main>

            <PreviewModal
                proof={previewing}
                onClose={() => setPreviewing(null)}
                onConfirmConsent={handleConsent}
            />
        </ReseauLayout>
    );
}
